import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
} from 'react'
import { useTenant } from './TenantContext'
import apiService from '../services/api.service'
import { updateBodyLanguageClass } from '../utils/languageHelper'

const LanguageContext = createContext({
  languageId: null,
  languageCode: null,
  languageContent: [],
  t: () => '',
  changeLanguage: () => {},
})

// Helper function to read language synchronously from localStorage
const getStoredLanguage = () => {
  try {
    const raw = localStorage.getItem('l')
    if (raw) {
      return JSON.parse(raw)
    }
  } catch (e) {
    console.error('Failed to read language from localStorage:', e)
  }
  return {}
}

export const LanguageProvider = ({ children }) => {
  const {
    tenant,
    switchLocale: tenantSwitchLocale,
    isLoading: tenantLoading,
  } = useTenant()
  // Initialize with stored language synchronously to avoid null on first render
  const [currentLanguage, setCurrentLanguage] = useState(() =>
    getStoredLanguage(),
  )
  const [isChangingLanguage, setIsChangingLanguage] = useState(false)

  // Expose languageId and languageCode from localStorage, fallback to tenant default
  const languageId = currentLanguage.languageid ?? tenant?.languageid ?? null
  const languageCode =
    currentLanguage.languagecode ?? tenant?.languagecode ?? null

  // Update body class when language changes
  useEffect(() => {
    if (languageCode) {
      updateBodyLanguageClass(languageCode)
    }
  }, [languageCode])

  const changeLanguage = async (
    language,
    tenanturlname,
    clientId = null,
    tenantId = null,
  ) => {
    // language: { languageid, languagecode }
    try {
      setIsChangingLanguage(true)

      // Update state and localStorage immediately
      setCurrentLanguage(language)
      localStorage.setItem('l', JSON.stringify(language))

      // Reload tenant with new language (await the promise)
      await tenantSwitchLocale(language, tenanturlname)

      // If client is authenticated, update their default language in backend (fire and forget)
      if (clientId && tenantId) {
        apiService
          .put('/api/clients/clientlanguageupdate', {
            tenantid: tenantId,
            clientid: clientId,
            languageid: language.languageid,
          })
          .catch((error) => {
            console.error(
              'Failed to update language preference in backend:',
              error,
            )
          })
      }
    } catch (error) {
      console.error('Error updating language:', error)
      // Still update UI even if backend call fails
    } finally {
      setIsChangingLanguage(false)
    }
  }

  const t = (keyPath, params = {}) => {
    try {
      const keys = keyPath.split('.')
      // Access v3 property from tenantlanguagecontent
      let value = tenant?.tenantlanguagecontent?.v3 || {}
      for (const k of keys) {
        if (value && typeof value === 'object' && k in value) value = value[k]
        else return keyPath
      }

      if (typeof value !== 'string') {
        return keyPath
      }

      if (!params || typeof params !== 'object') {
        return value
      }

      return value.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, tokenName) => {
        const replacement = params[tokenName]
        return replacement === undefined || replacement === null
          ? match
          : String(replacement)
      })
    } catch (error) {
      console.error('Translation error for key:', keyPath, error)
      return keyPath
    }
  }

  const languageContent = tenant?.tenantlanguagecontent?.v3 || {}
  const languageCodeDate =
    tenant?.tenantlanguagecontent?.languagecodedate || 'en-US'

  const value = useMemo(
    () => ({
      languageId,
      languageCode,
      languageCodeDate,
      languageContent,
      changeLanguage,
      isChangingLanguage: isChangingLanguage || tenantLoading,
      t,
    }),
    [
      languageId,
      languageCode,
      languageCodeDate,
      languageContent,
      isChangingLanguage,
      tenantLoading,
    ],
  )

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  )
}

export const useLanguage = () => {
  const ctx = useContext(LanguageContext)
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider')
  return ctx
}
